/**
 * JSON-LD @graph generator for framework detail pages.
 *
 * Emits Article + DefinedTerm + FAQPage + BreadcrumbList + Organization
 * as a single @graph so Google and AI engines can resolve the framework
 * as one connected entity. All nodes share `@id` references to link them.
 *
 * The DefinedTerm node is what teaches Google's knowledge graph that a
 * framework like "GROW Model" is a recognized concept; FAQPage drives PAA
 * snippets and is the most-cited schema in AEO answers.
 *
 * The Organization node is a stub that resolves to the canonical brand entity
 * via the shared BRAND_ORGANIZATION_ID; the fully-described Organization lives
 * in the site-wide schema. Both must use that exact @id or the Knowledge Graph
 * splits the brand into two entities. The CCO node below is a *different*
 * organization and keeps its own @id.
 *
 * Aliases: framework jsonld, framework structured data, ld graph, defined
 * term, framework schema, AEO graph
 */
import { BRAND, BRAND_ORGANIZATION_ID, BRAND_URL } from "~/lib/shared/brand";
import { generateSlug } from "~/lib/shared/slug";

const FRAMEWORK_SET_ID = `${BRAND_URL}/frameworks#coaching-frameworks-set`;

type Faq = { readonly question: string; readonly answer: string };

type Cco = {
  readonly name: string;
  readonly slug: string | null;
} | null;

export type FrameworkJsonLdInput = {
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  readonly typeLabel: string;
  readonly cco: Cco;
  readonly faqs: ReadonlyArray<Faq>;
  readonly publishedAt: Date | null;
  readonly updatedAt: Date | null;
};

/** Best-effort acronym detection: GROW, SCARF, COR.E. Returns null otherwise. */
function deriveTermCode(name: string): string | null {
  const head = name.split(/\s+/)[0]?.replace(/\.$/, "") ?? "";
  // Up to 6 letters, all caps (allows mixed dot like COR.E).
  if (/^[A-Z][A-Z.]{1,5}$/.test(head)) return head.replace(/\./g, "");
  return null;
}

export function buildFrameworkJsonLd(input: FrameworkJsonLdInput) {
  const url = `${BRAND_URL}/frameworks/${input.slug}`;
  const articleId = `${url}#article`;
  const definedTermId = `${url}#defined-term`;
  const breadcrumbId = `${url}#breadcrumb`;

  const datePublished = input.publishedAt?.toISOString();
  const dateModified =
    input.updatedAt?.toISOString() ?? input.publishedAt?.toISOString();
  const termCode = deriveTermCode(input.name);

  // Resolve CCO up front so the Article node can reference it at
  // construction time instead of being patched after the array is built.
  const ccoNode = buildCcoNode(input.cco);

  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "Organization",
      "@id": BRAND_ORGANIZATION_ID,
      name: BRAND.name,
      url: BRAND_URL,
      logo: {
        "@type": "ImageObject",
        url: BRAND.logo,
      },
    },
    {
      "@type": "Article",
      "@id": articleId,
      headline: input.name,
      description: input.description,
      url,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      about: { "@id": definedTermId },
      author: { "@id": BRAND_ORGANIZATION_ID },
      publisher: { "@id": BRAND_ORGANIZATION_ID },
      ...(ccoNode ? { creator: { "@id": ccoNode["@id"] } } : {}),
      ...(datePublished ? { datePublished } : {}),
      ...(dateModified ? { dateModified } : {}),
      inLanguage: "en",
      articleSection: input.typeLabel,
    },
    {
      "@type": "DefinedTerm",
      "@id": definedTermId,
      name: input.name,
      description: input.description,
      url,
      ...(termCode ? { termCode } : {}),
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        "@id": FRAMEWORK_SET_ID,
        name: "Coaching Frameworks",
        url: `${BRAND_URL}/frameworks`,
      },
    },
    {
      "@type": "BreadcrumbList",
      "@id": breadcrumbId,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BRAND_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Frameworks",
          item: `${BRAND_URL}/frameworks`,
        },
        { "@type": "ListItem", position: 3, name: input.name, item: url },
      ],
    },
    ...(input.faqs.length > 0
      ? [
          {
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            mainEntity: input.faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          },
        ]
      : []),
    ...(ccoNode ? [ccoNode] : []),
  ];

  return { "@context": "https://schema.org", "@graph": graph };
}

function buildCcoNode(cco: Cco): Record<string, unknown> | null {
  if (!cco?.name) return null;
  const ccoUrl = cco.slug
    ? `${BRAND_URL}/organizations/${cco.slug}`
    : undefined;
  const ccoId = ccoUrl
    ? `${ccoUrl}#organization`
    : `${BRAND_URL}#cco-${generateSlug(cco.name)}`;
  return {
    "@type": "Organization",
    "@id": ccoId,
    name: cco.name,
    ...(ccoUrl ? { url: ccoUrl } : {}),
  };
}
